"use client";

import React from "react";
import { DOCXViewer as BaseDOCXViewer } from "@webuild-ai/react-ui/components";

import { findTextMatch, normalizeText } from "@/utils/textMatching";
import { logger } from "@/telemetry";

export interface DOCXViewerProps {
  /** DOCX document as ArrayBuffer */
  documentData: ArrayBuffer;

  /** Text to find and highlight in the DOCX */
  citationText?: string;
}

/**
 * DOCX Viewer component for Citations
 * Extends the shared DOCXViewer with citation text highlighting
 */
export const DOCXViewer: React.FC<DOCXViewerProps> = ({ documentData, citationText }) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [documentText, setDocumentText] = React.useState<string>("");

  /**
   * Callback when document is rendered
   * Store the container reference and document text for highlighting
   */
  const handleRendered = React.useCallback((container: HTMLDivElement, extractedText: string) => {
    containerRef.current = container;
    setDocumentText(extractedText);
  }, []);

  // Find and highlight citation text
  React.useEffect(() => {
    if (!citationText || !documentText || !containerRef.current) return;

    logger.info("DOCX: Attempting to find and highlight", {
      citationTextLength: citationText.length,
      documentTextLength: documentText.length,
      citationPreview: citationText.substring(0, 100),
    });

    const matchResult = findTextMatch(documentText, citationText);

    logger.info("DOCX: Match result", { matchResult: String(matchResult) });

    if (matchResult) {
      // Wait a bit for docx-preview to fully render before highlighting
      setTimeout(() => {
        if (!containerRef.current) return;
        logger.info("DOCX: Starting highlight in DOM (after delay)");
        highlightTextInDOM(containerRef.current, citationText);

        // Check if marks are still there after 1 second
        setTimeout(() => {
          const marks = containerRef.current?.querySelectorAll("mark");

          logger.info("DOCX: After 1s, mark elements in DOM", { markCount: marks?.length || 0 });
        }, 1000);
      }, 500); // 500ms delay
    } else {
      logger.info("DOCX: No match found (document will display without highlighting)");
    }
  }, [citationText, documentText]);

  /**
   * Highlight matched text in the rendered DOCX DOM
   */
  const highlightTextInDOM = (container: HTMLElement, citationText: string) => {
    try {
      // Get all text nodes in the document
      const textNodes: Text[] = [];

      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);

      let node;

      while ((node = walker.nextNode())) {
        if (node.nodeValue && node.nodeValue.trim().length > 0) {
          textNodes.push(node as Text);
        }
      }

      if (textNodes.length === 0) {
        logger.info("No text nodes found in DOCX container (document may still be rendering)");

        return;
      }

      // Build normalised text with character-to-node mapping (same approach as PDF viewer)
      const isWordChar = (ch: string) => /[\p{L}\p{N}]/u.test(ch);
      const charToNodeIndex: number[] = [];
      let fullText = "";

      textNodes.forEach((node, idx) => {
        const curr = normalizeText(node.nodeValue || "");
        const prev = idx > 0 ? normalizeText(textNodes[idx - 1].nodeValue || "") : "";

        // Smart spacing: insert space only if needed
        const prevLast = prev.slice(-1);
        const currFirst = curr.slice(0, 1);
        const shouldInsertSpace =
          idx > 0 && isWordChar(prevLast) && isWordChar(currFirst) && prevLast !== "-";

        if (shouldInsertSpace) {
          fullText += " ";
          charToNodeIndex.push(-1); // separator, belongs to no node
        }

        // Append current normalised text and map each character to this node index
        for (let k = 0; k < curr.length; k++) {
          fullText += curr[k];
          charToNodeIndex.push(idx);
        }
      });

      // Find citation in the full text using head and tail segments
      const headLen = Math.min(100, citationText.length);
      const headSeg = citationText.substring(0, headLen);
      const tailLen = Math.min(100, citationText.length);
      const tailSeg = citationText.substring(Math.max(0, citationText.length - tailLen));

      const headMatch = findTextMatch(fullText, headSeg);
      const tailMatch = findTextMatch(fullText, tailSeg);

      let startNodeIndex = 0;
      let endNodeIndex = textNodes.length - 1;

      // Map start position to node index using charToNodeIndex
      if (headMatch) {
        let pos = Math.max(0, Math.min(headMatch.position, fullText.length - 1));
        let idx = charToNodeIndex[pos];

        // If on separator, scan forward then backward
        if (idx < 0) {
          for (let p = pos; p < charToNodeIndex.length; p++) {
            if (charToNodeIndex[p] >= 0) {
              idx = charToNodeIndex[p];
              break;
            }
          }
          if (idx < 0) {
            for (let p = pos; p >= 0; p--) {
              if (charToNodeIndex[p] >= 0) {
                idx = charToNodeIndex[p];
                break;
              }
            }
          }
        }

        if (idx >= 0) {
          startNodeIndex = idx;
        }
      }

      // Map end position to node index using charToNodeIndex
      if (tailMatch) {
        let endPos = tailMatch.position + tailMatch.length - 1;

        endPos = Math.max(0, Math.min(endPos, fullText.length - 1));
        let idx = charToNodeIndex[endPos];

        // If on separator, prefer backward scan (backward bias to avoid overshooting)
        if (idx < 0) {
          for (let p = endPos; p >= 0; p--) {
            if (charToNodeIndex[p] >= 0) {
              idx = charToNodeIndex[p];
              break;
            }
          }
          if (idx < 0) {
            for (let p = endPos; p < charToNodeIndex.length; p++) {
              if (charToNodeIndex[p] >= 0) {
                idx = charToNodeIndex[p];
                break;
              }
            }
          }
        }

        if (idx >= 0) {
          endNodeIndex = idx;
        }
      }

      // Highlight nodes from startNodeIndex to endNodeIndex
      const overlappingNodes: Text[] = [];

      for (let i = startNodeIndex; i <= endNodeIndex; i++) {
        overlappingNodes.push(textNodes[i]);
      }

      logger.info("DOCX: Highlighting nodes", {
        startNodeIndex,
        endNodeIndex,
        overlappingNodesCount: overlappingNodes.length,
        textNodesCount: textNodes.length,
      });

      // Highlight overlapping nodes
      let firstMark: HTMLElement | undefined;
      let highlightedCount = 0;
      let skippedCount = 0;

      overlappingNodes.forEach((node, index) => {
        const parent = node.parentElement;

        if (!parent) {
          skippedCount++;
          logger.info("DOCX: Node has no parent (skipping)", { nodeIndex: index });

          return;
        }

        // Create highlight element
        const mark = document.createElement("mark");

        mark.className = "bg-warning/30 dark:bg-warning/40 px-1 rounded";
        // Remove hardcoded backgroundColor to let Tailwind handle dark mode
        mark.style.padding = "2px 4px";
        mark.style.borderRadius = "2px";

        // Replace text node with highlighted version
        const textContent = node.nodeValue || "";

        mark.textContent = textContent;

        try {
          parent.replaceChild(mark, node);
          highlightedCount++;
        } catch (err) {
          skippedCount++;
          logger.error(`DOCX: Failed to replace node ${index}`, err);
        }

        // Keep reference to first mark for scrolling
        if (!firstMark) {
          firstMark = mark;
        }
      });

      logger.info("DOCX: Highlight complete", { highlightedCount, skippedCount });

      // Scroll to first highlighted element
      if (firstMark) {
        // Use setTimeout to ensure DOM updates are complete and allow smooth scrolling
        setTimeout(() => {
          firstMark?.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
        }, 100);
      }
    } catch (err) {
      logger.error("Failed to highlight text in DOCX", err);
    }
  };

  return (
    <BaseDOCXViewer
      documentData={documentData}
      loadingLabel="Loading DOCX..."
      onError={(err) => logger.error("Failed to render DOCX", err)}
      onRendered={handleRendered}
    />
  );
};

export default DOCXViewer;
